import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/main-layout";
import Loader from "../Components/loader/loader";
import Grid from "../Components/Grid/grid";
import { motion } from "framer-motion";
import OurWorkCard from "../Components/card/our-work-card";
import WorkCaseStudyContactUs from "../Components/common-section/work-case-study-contact-us-section";
import { Filter, X } from "lucide-react";
import MultiSelect from "../Components/input/multi-select";

function CaseStudyPage() {
  const navigate = useNavigate();
  const [works, setWorks] = useState([]);
  const [filteredWorks, setFilteredWorks] = useState([]);
  const [visibleWorks, setVisibleWorks] = useState(4);
  const [loading, setLoading] = useState(true);
  const [selectedIndustries, setSelectedIndustries] = useState<
    (string | number)[]
  >([]);
  const [selectedTechStack, setSelectedTechStack] = useState<
    (string | number)[]
  >([]);
  const [selectedSort, setSelectedSort] = useState<string | number | null>(
    null
  );
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = () => {
    axios
      .get(`case_studies`)
      .then((res) => {
        if (res.status === 200) {
          const caseStudies = res.data.filter(
            (data: any) => data.is_case_studies === true
          );
          setLoading(false);
          setWorks(caseStudies);
        }
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const industries = Array.from(
    new Set(works.map((data: any) => data?.domain).filter(Boolean))
  ).map((domain) => ({
    name: domain,
    label: domain,
    value: domain,
  }));

  const techStack = Array.from(
    new Set(
      works
        .flatMap((t: any) => [t.backend_tech, t.frontend_tech, t.database_tech])
        .filter(Boolean)
    )
  ).map((name) => ({
    name,
    label: name,
    value: name,
  }));

  const sortOnDuration = [
    { name: "Latest", label: "Latest", value: "latest" },
    { name: "Oldest", label: "Oldest", value: "oldest" },
  ];

  useEffect(() => {
    let filtered = [...works];

    if (selectedIndustries.length > 0) {
      filtered = filtered.filter((work: any) =>
        selectedIndustries.includes(work.domain)
      );
    }

    if (selectedTechStack.length > 0) {
      filtered = filtered.filter((work: any) => {
        const workTechs = [
          work.backend_tech,
          work.frontend_tech,
          work.database_tech,
        ].filter(Boolean);
        return selectedTechStack.some((tech) => workTechs.includes(tech));
      });
    }

    if (selectedSort) {
      filtered.sort((a: any, b: any) => {
        if (selectedSort === "latest") {
          return (
            new Date(b.created_at || b.id).getTime() -
            new Date(a.created_at || a.id).getTime()
          );
        } else if (selectedSort === "oldest") {
          return (
            new Date(a.created_at || a.id).getTime() -
            new Date(b.created_at || b.id).getTime()
          );
        }
        return 0;
      });
    }

    setFilteredWorks(filtered);
    setVisibleWorks(4);
  }, [works, selectedIndustries, selectedTechStack, selectedSort]);

  const clearFilters = () => {
    setSelectedIndustries([]);
    setSelectedTechStack([]);
    setSelectedSort(null);
  };

  const hasActiveFilters =
    selectedIndustries.length > 0 ||
    selectedTechStack.length > 0 ||
    selectedSort !== null;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowFilters(true);
      } else {
        setShowFilters(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  if (loading)
    return (
      <section className="w-full min-h-screen h-full flex flex-col items-start justify-center lg:p-16 p-8 mt-16">
        <Loader />
      </section>
    );
  return (
    <MainLayout>
      <Grid size={100} direction="top-center" />
      <section className="w-full min-h-screen h-full flex flex-col items-center justify-center lg:p-16 p-8 mt-32">
        <div className="relative z-10 flex flex-col items-center text-center w-full max-w-4xl">
          <motion.h2
            className="text-4xl md:text-6xl font-bold text-black capitalize"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Case Studies
          </motion.h2>
          <motion.p
            className="mt-4 text-lg text-gray-600 max-w-2xl"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
          >
            Discover how our custom software solutions have helped businesses
            across industries drive growth and digital transformation.
          </motion.p>
        </div>

        <div className="relative flex flex-col justify-center items-center w-full max-w-6xl mt-32">
          <div className="md:hidden w-full mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                hasActiveFilters
                  ? "bg-blue-50 border-blue-200 text-blue-700"
                  : "bg-white border-gray-300 text-gray-700"
              } hover:bg-gray-50`}
            >
              <Filter size={18} />
              <span>
                {showFilters ? "Hide Filters" : "Show Filters"}
                {hasActiveFilters && " (Active)"}
              </span>
            </button>
          </div>
          <div className="flex flex-col items-center justify-center">
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                <MultiSelect
                  options={industries}
                  selectedValues={selectedIndustries}
                  onChange={(val: any) => setSelectedIndustries(val)}
                  labelKey="label"
                  valueKey="value"
                  placeholder="Select Industry"
                  className="w-full md:w-52"
                />
                <MultiSelect
                  options={techStack}
                  selectedValues={selectedTechStack}
                  onChange={(val: any) => setSelectedTechStack(val)}
                  labelKey="label"
                  valueKey="value"
                  placeholder="Select Technology"
                  className="w-full md:w-52"
                />
                <MultiSelect
                  options={sortOnDuration}
                  selectedValue={selectedSort}
                  onChange={(val: any) => setSelectedSort(val)}
                  labelKey="label"
                  valueKey="value"
                  placeholder="Sort by"
                  className="w-full md:w-52"
                  multiple={false}
                />
              </div>
            )}
            <div className="flex items-center gap-4 my-5">
              {hasActiveFilters && (
                <motion.button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <X size={16} />
                  Clear Filters
                </motion.button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {filteredWorks.slice(0, visibleWorks).map((data: any, index) => (
              <OurWorkCard
                index={index}
                data={data}
                handleNavigate={() =>
                  navigate(`/case-study/${data?.id}/${data?.title}`)
                }
                key={index}
              />
            ))}
          </div>
        </div>
        {visibleWorks < filteredWorks.length && (
          <motion.button
            onClick={() => setVisibleWorks((prev) => prev + 2)}
            className="mt-8 px-6 py-3 text-lg font-semibold text-white bg-primary rounded-full shadow-md hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:outline-none transition"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            View More
          </motion.button>
        )}
      </section>
      <WorkCaseStudyContactUs />
    </MainLayout>
  );
}

export default CaseStudyPage;
